import { Option as AutocompleteOption } from '@/components/inputs/AutocompleteInput';
import { TextInput, GlobalButton, AutocompleteInput } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { ServicesFilter, defaultServicesFilter } from '../models';
import { Search as SearchIcon } from '@mui/icons-material';
import { useState, useEffect, useMemo } from 'react';
import useDebounce from '@/hooks/useDebounce';

export interface ServicesSearchBarOptions {
  businessOptions: AutocompleteOption[];
  serviceTypeOptions: AutocompleteOption[];
  vehicleTypeOptions: AutocompleteOption[];
  serviceTypesLoading: boolean;
  vehicleTypesLoading: boolean;
}

interface SearchBarProps {
  filters: ServicesFilter;
  onFiltersChange: (filters: ServicesFilter) => void;
  options: ServicesSearchBarOptions;
  onBusinessSearchInputChange?: (value: string) => void;
  hideBusinessFilter?: boolean;
  hideServiceTypeFilter?: boolean;
  hideVehicleTypeFilter?: boolean;
}

const SearchBar = ({
  filters,
  onFiltersChange,
  options,
  onBusinessSearchInputChange,
  hideBusinessFilter,
  hideServiceTypeFilter,
  hideVehicleTypeFilter,
}: SearchBarProps) => {
  const [name, setName] = useState(filters.name || '');
  const debouncedName = useDebounce(name, 350);
  const [serviceTypeSearch, setServiceTypeSearch] = useState('');
  const [vehicleTypeSearch, setVehicleTypeSearch] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState<AutocompleteOption | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<AutocompleteOption | null>(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState<AutocompleteOption | null>(null);

  const {
    businessOptions,
    serviceTypeOptions: allServiceTypeOptions,
    vehicleTypeOptions: allVehicleTypeOptions,
    serviceTypesLoading,
    vehicleTypesLoading,
  } = options;

  const serviceTypeOptions = useMemo(
    () =>
      allServiceTypeOptions.filter((opt) =>
        opt.label.toLowerCase().includes(serviceTypeSearch.toLowerCase())
      ),
    [allServiceTypeOptions, serviceTypeSearch]
  );

  const vehicleTypeOptions = useMemo(
    () =>
      allVehicleTypeOptions.filter((opt) =>
        opt.label.toLowerCase().includes(vehicleTypeSearch.toLowerCase())
      ),
    [allVehicleTypeOptions, vehicleTypeSearch]
  );

  useEffect(() => {
    setName(filters.name || '');
  }, [filters.name]);

  useEffect(() => {
    onFiltersChange({
      name: debouncedName || undefined,
      serviceTypeId: selectedServiceType?.data?.id || undefined,
      vehicleTypeId: selectedVehicleType?.data?.id || undefined,
      commercialBusinessId: selectedBusiness?.data?.id || undefined,
    });
  }, [debouncedName, onFiltersChange]);

  const applyFilters = () => {
    onFiltersChange({
      name: name || undefined,
      serviceTypeId: selectedServiceType?.data?.id || undefined,
      vehicleTypeId: selectedVehicleType?.data?.id || undefined,
      commercialBusinessId: selectedBusiness?.data?.id || undefined,
    });
  };

  const clearFilters = () => {
    setName(defaultServicesFilter.name || '');
    setSelectedBusiness(null);
    setSelectedServiceType(null);
    setSelectedVehicleType(null);
    setServiceTypeSearch('');
    setVehicleTypeSearch('');
    onFiltersChange({ ...defaultServicesFilter });
  };

  return (
    <div className="w-full p-5 flex flex-col justify-center bg-white rounded shadow-sm gap-5">
      <div className="flex gap-2.5">
        <TextInput
          type="search"
          name="service-name-search"
          placeholder="Buscar servicio"
          value={name || ''}
          onChange={(val) => setName(val.toString())}
        />
        {!hideBusinessFilter && (
          <AutocompleteInput
            name="services-filter-business-input"
            placeholder="Compañía/Negocio"
            value={selectedBusiness}
            onChange={setSelectedBusiness}
            onInputChange={onBusinessSearchInputChange ?? (() => {})}
            options={businessOptions}
          />
        )}
      </div>
      <div className="flex gap-2.5">
        {!hideServiceTypeFilter && (
          <AutocompleteInput
            name="services-filter-service-type"
            placeholder={
              serviceTypesLoading ? 'Cargando...' : 'Tipo de servicio'
            }
            value={selectedServiceType}
            onChange={setSelectedServiceType}
            onInputChange={setServiceTypeSearch}
            options={serviceTypeOptions}
          />
        )}
        {!hideVehicleTypeFilter && (
          <AutocompleteInput
            name="services-filter-vehicle-type"
            placeholder={
              vehicleTypesLoading ? 'Cargando...' : 'Tipo de vehículo'
            }
            value={selectedVehicleType}
            onChange={setSelectedVehicleType}
            onInputChange={setVehicleTypeSearch}
            options={vehicleTypeOptions}
          />
        )}
      </div>
      <div className="flex gap-2.5 justify-end">
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={clearFilters}
        >
          Borrar filtros
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={applyFilters}
        >
          <SearchIcon /> Buscar
        </GlobalButton>
      </div>
    </div>
  );
};

export default SearchBar;
